import {Button, Col, Row, Spin, Table} from "antd";
import React, {useContext, useEffect, useState} from "react";
import {Patient, PatientListResponse} from "../api-client";
import {getCalendarApi, getPatientApi} from "../api/apiInstances";
import {PlusOutlined} from "@ant-design/icons";
import {FormattedMessage} from "react-intl";
import {isMobileOnly} from 'react-device-detect';
import '../assets/css/Dentobook.css';
import {Link, useNavigate} from "react-router-dom";
import dayjs from "dayjs";
import UserIcon from "../components/common/UserIcon";
import DetailWrapper from "../components/common/DetailWrapper";
import InfiniteScroll from "react-infinite-scroll-component";
import {accessibilityRoles} from "../utils/accessUtils";
import AccessControl from "../components/common/AccessControl";
import PatientCreateModal from "../components/patient/PatientCreateModal";
import SearchForm from "../components/common/SearchForm";
import { dateToLocalISO } from "../utils/calendarUtils";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import logger from "../utils/logger";

interface PatientItem {
    id: string;
    key: string;
    name: string;
    lastHour: string;
    nextHour: string;
}

// Type for patient list items: API response augmented with display fields
type PatientListItem = PatientListResponse & {
    key: string;
    name: string;
    lastHour: string;
    nextHour: string;
};

const Patients = () => {

    const {principal} = useContext(DentobookApplicationContext);

    const [keyword, setKeyword] = useState<string>('');
    const [sortBy, setSortBy] = useState<('id' | 'name' | 'lastHour' | 'nextHour')>('id');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');

    const [patients, setPatients] = useState<PatientListItem[]>([]);
    const [initialLoading, setInitialLoading] = useState<boolean>(true);

    const [totalElements, setTotalElements] = useState<number>(0);

    const [isPatientCreateModalOpen, setIsPatientCreateModalOpen] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [height, setHeight] = useState<number>(window.innerHeight);

    const calendarApi = getCalendarApi();
    const patientApi = getPatientApi();

    const navigate = useNavigate();

    useEffect(() => {
        setHeight(window.innerHeight * 0.01);
        const handleResize = () => {
            setHeight(window.innerHeight * 0.01);
        };
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        loadData(true, sortBy, keyword);
    }, []);

    const loadData = (
        refresh: boolean,
        sortBy: 'id' | 'name' | 'lastHour' | 'nextHour',
        keyword: string
    ) => {
        if (refresh) {
            setPage(0);
        } else {
            setPage(prevState => prevState + 1);
        }

        patientApi.fetchAllPatients(keyword, sortBy, order, refresh ? 0 : page + 1, 15).then(async (response) => {
            logger.log(response);

            // Check if required response properties exist
            if (
                typeof response.data.pagesCount === 'undefined' ||
                typeof response.data.patients === 'undefined' ||
                typeof response.data.totalElements === 'undefined'
            ) {
                setInitialLoading(false);
                return;
            }

            setTotalElements(response.data.totalElements);

            const patientsList: PatientListResponse[] = response.data.patients;

            if (patientsList.length === 0) {
                if (refresh) {
                    setPatients([]);
                }
                setInitialLoading(false);
                return;
            }

            // Get all patient IDs
            const patientIds = patientsList.filter(p => p.id != null).map(p => p.id as string);

            // Batch fetch all calendar events for these patients using generated API client
            let eventsByPatient: Record<string, any[]> = {};
            try {
                const calendarApi = getCalendarApi();
                const eventsResponse = await calendarApi.batchGetEventsByPatients({
                    patient_ids: patientIds
                });
                eventsByPatient = eventsResponse.data;
            } catch (error) {
                logger.error('Failed to batch fetch calendar events:', error);
                // Continue with empty events
            }

            const now = dateToLocalISO(new Date());

            // Process each patient with their events (no more individual API calls)
            const itemsData = patientsList.map((patient: PatientListResponse) => {
                // PatientListResponse doesn't include lastHour/nextHour, so we start fresh
                let lastHour = "";
                let nextHour = "";
                let hasPastEvent = false;
                let hasFutureEvent = false;

                if (patient.id != null) {
                    const calendarEvents = eventsByPatient[patient.id] || [];

                    if (calendarEvents.length === 0) {
                        // No events, keep lastHour and nextHour empty
                    } else {
                        calendarEvents.forEach((event: any) => {
                            const eventStart = dayjs(event.start);
                            const eventEnd = dayjs(event.end);

                            if (eventEnd.isBefore(now)) {
                                hasPastEvent = true;
                                if (!lastHour || eventEnd.isAfter(dayjs(lastHour))) {
                                    lastHour = eventEnd.toISOString();
                                }
                            } else if (eventStart.isAfter(now)) {
                                hasFutureEvent = true;
                                if (!nextHour || eventStart.isBefore(dayjs(nextHour))) {
                                    nextHour = eventStart.toISOString();
                                }
                            }
                        });

                        lastHour = hasPastEvent ? lastHour : "";
                        nextHour = hasFutureEvent ? nextHour : "";
                    }
                }

                // Format lastHour and nextHour for display
                return {
                    key: patient.id,
                    ...patient,
                    name: `${patient.firstName} ${patient.lastName}`,
                    lastHour: lastHour ? dayjs(lastHour).format('DD.MM.YYYY') : "",
                    nextHour: nextHour ? dayjs(nextHour).format('DD.MM.YYYY') : ""
                };
            });

            if (refresh) {
                setPatients(itemsData);
            } else {
                setPatients(patients.concat(itemsData));
            }
            setInitialLoading(false);
        }).catch(error => {
            logger.log("Error loading patients:", error);
            setInitialLoading(false);
        });
    }

    return (
        <AccessControl
            notAccessibleFor={[accessibilityRoles.PLATFORM_ADMINISTRATOR, accessibilityRoles.PATIENT]}>
            <PatientCreateModal isModalOpen={isPatientCreateModalOpen}
                                setIsModalOpen={setIsPatientCreateModalOpen}
            />
            <DetailWrapper navigateBack={undefined} removeVerticalScroll={true}>
                <Row className="mb-2">
                    <Col span={12}>
                        <h3><FormattedMessage id="patients"/></h3>
                    </Col>
                    <Col span={12}>
                        <div className={isMobileOnly ? "mobile-btn-wrapper" : ""}>
                            <Button shape="round"
                                    id="add-button"
                                    className={isMobileOnly ? "mobile-add-btn" : "dentobook-btn-primary dentobook-btn-medium"}
                                    style={{float: 'right', marginTop: '24px'}}
                                    onClick={() => {
                                        if (principal?.features.includes('PATIENT_SELF_REGISTRY')) {
                                            setIsPatientCreateModalOpen(true);
                                        } else {
                                            navigate('/patients/create');
                                        }
                                    }}
                                    type="primary"><PlusOutlined/> <FormattedMessage id="add"/></Button>
                        </div>
                    </Col>
                </Row>
                <SearchForm entity="patient"
                            keyword={keyword}
                            setKeyword={setKeyword}
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                            loadData={loadData}
                />
                {isMobileOnly ? <div className="horizontal-scroll-indicator"></div> : null}
                {initialLoading ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin tip={<FormattedMessage id="loading" />} />
                    </div>
                ) : (
                    <div id="scrollableDiv" style={{
                        height: `${height * 80 - (!isMobileOnly ? 170 : 300)}px`,
                        width: isMobileOnly ? '90%' : '97%',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        position: 'absolute'
                    }}>
                        <InfiniteScroll
                        scrollableTarget="scrollableDiv"
                        dataLength={patients.length}
                        next={() => loadData(false, sortBy, keyword)}
                        hasMore={patients.length < totalElements}
                        style={{overflow: 'hidden'}}
                        loader={<div style={{ textAlign: 'center', padding: '20px' }}><Spin size="small" /></div>}
                        refreshFunction={() => loadData(true, sortBy, keyword)}
                        pullDownToRefresh
                    >
                        <Table dataSource={patients}
                               pagination={false}
                               className="dentobook-table dentobook-scrollable-table"
                               onRow={(patient) => {
                                   return {
                                       onClick: () => {
                                           navigate('/patients/' + patient.id);
                                       }
                                   };
                               }}>
                            <Table.Column key="name" width="20%" render={(patient) => {
                                const avatarSrc = patient.avatarUrl;
                                return (
                                    <div className="username-icon">
                                        {!avatarSrc ? <UserIcon size="small"/> :
                                            <img className="thumbnail-img"
                                                 width={10}
                                                 src={avatarSrc}
                                                 alt="avatar"/>
                                        }
                                        <span className="font-weight-500">{patient.name}</span>
                                    </div>
                                );
                            }}
                                          title={<FormattedMessage id="name"/>}
                                          sorter={(patient1: PatientItem, patient2: PatientItem, sortOrder) => {
                                              setSortBy("name");
                                              setOrder(sortOrder === 'ascend' ? 'asc' : 'desc');

                                              return patient1.name.localeCompare(patient2.name);
                                          }}
                            />
                            <Table.Column key="id" width="20%" title="ID"
                                          sorter={(patient1: PatientItem, patient2: PatientItem, sortOrder) => {
                                              setSortBy("id");
                                              setOrder(sortOrder === 'ascend' ? 'asc' : 'desc');

                                              return patient1.id.localeCompare(patient2.id);
                                          }}
                                          render={(patient) => <span
                                              className="font-weight-500">{patient.id.substring(0, 5)}</span>}
                            />
                            <Table.Column key="phone"
                                          width="15%"
                                          render={(patient) => <span className="font-weight-500">{patient.phone}</span>}
                                          title={<FormattedMessage id="phone"/>}/>
                            <Table.Column key="email" title="Email"
                                          width="15%"
                                          render={(patient) => <span className="font-weight-500">{patient.email}</span>}
                            />
                            <Table.Column key="lastAppointment"
                                          width="15%"
                                          title={<FormattedMessage id="lastAppointment"/>}
                                          sorter={(patient1: PatientItem, patient2: PatientItem, sortOrder) => {
                                              setSortBy("lastHour");
                                              setOrder(sortOrder === 'ascend' ? 'asc' : 'desc');

                                              return patient1.lastHour.localeCompare(patient2.lastHour);
                                          }}
                                          render={(patient) => (
                                              <Link to={"/patients/" + patient.id}><span
                                                  className="font-weight-700">{patient.lastHour}</span></Link>
                                          )}
                            />
                            <Table.Column key="nextAppointment"
                                          width="15%"
                                          title={<FormattedMessage id="nextAppointment"/>}
                                          sorter={(patient1: PatientItem, patient2: PatientItem, sortOrder) => {
                                              setSortBy("nextHour");
                                              setOrder(sortOrder === 'ascend' ? 'asc' : 'desc');

                                              return patient1.nextHour.localeCompare(patient2.nextHour);
                                          }}
                                          render={(patient) => (
                                              <Link to={"/patients/" + patient.id}><span
                                                  className="font-weight-700">{patient.nextHour}</span></Link>
                                          )}
                            />
                        </Table>
                    </InfiniteScroll>
                </div>
                )}
                <div id="bottom-div" style={{bottom: 0, position: 'absolute'}}></div>
            </DetailWrapper>
        </AccessControl>
    )
};

export default Patients;